// src/client/layouts/AppShell.tsx
//
// Phase B T2a/T2d: app shell の最小構成 + locale toggle.
//  - header: title + LocaleSwitcher + (将来) user 表示
//  - main: <Outlet /> で子 route を表示
//  - Phase B' T10: <Suspense> で route lazy chunk の load fallback を提供

import { Suspense, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocaleSwitcher } from "../components/LocaleSwitcher";
import { ContrastToggle } from "../components/ContrastToggle";
import { useAuth } from "../providers/AuthProvider";

export function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // #701: server で今の token を失効させてから login へ (失敗しても端末の session は必ず消える)。
  //       POST 中は button を無効にする (連打で二重に呼ばない)。
  const [loggingOut, setLoggingOut] = useState(false);
  function handleLogout(): void {
    if (loggingOut) return;
    setLoggingOut(true);
    void logout().finally(() => {
      setLoggingOut(false);
      navigate("/login", { replace: true });
    });
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b bg-card">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-4 px-4 py-3">
          <Link
            to={isAuthenticated ? "/" : "/login"}
            className="text-lg font-semibold hover:opacity-80"
            aria-label={t("app.title")}
          >
            {t("app.title")}
          </Link>
          <div className="flex items-center gap-3">
            <LocaleSwitcher />
            {/* #706 (gap-g5): 屋外向けの高コントラスト表示 (手動) */}
            <ContrastToggle />
            <div className="text-sm text-muted-foreground">
              {isAuthenticated && user ? user.displayName : t("auth.signedOut")}
            </div>
            {isAuthenticated && (
              <>
                <Link
                  to="/change-password"
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {t("nav.changePassword", { defaultValue: "パスワード変更" })}
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  aria-busy={loggingOut}
                  className="rounded-md border bg-background px-3 py-1 text-xs font-medium hover:bg-muted"
                  aria-label={t("nav.logout", { defaultValue: "ログアウト" })}
                >
                  {t("nav.logout", { defaultValue: "ログアウト" })}
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Suspense
          fallback={
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("common.loading")}
            </p>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
